import {ActivationState, Client} from '@stomp/stompjs';
import {JSONContent} from '@tiptap/react';
import {MessageThreadScope, MessageThreadSummary} from '@/api/dto/message';
import {NotificationDto} from "@/api/dto/notification";

export default class WebSocketApi {
  private client: Client;

  constructor() {
    this.client = new Client({
      brokerURL: process.env.REACT_APP_CARECO_API_WS_BASE_URL,
    });
  }

  activate() {
    this.client.activate();
  }

  isReady() {
    return this.client.state === ActivationState.ACTIVE && this.client.connected;
  }

  onError(callback: (message: string) => void) {
    this.client.onStompError = (frame) => {
      callback(frame.headers.message);
    };
  }

  publishChatMessage(
    messageThreadId: number | null,
    clientId: string,
    careJourneyInstanceId: string | null,
    referralId: string | null,
    unitId: string | null,
    careJourneyRejectedReferralId: number | null,
    messageAttachmentIds: number[] | null,
    scope: MessageThreadScope,
    subject: string | null,
    content: JSONContent,
  ) {
    this.client.publish({
      destination: '/api/ws/event/chat/message',
      body: JSON.stringify({
        messageThreadId,
        clientId,
        careJourneyInstanceId,
        referralId,
        unitId,
        careJourneyRejectedReferralId,
        messageAttachmentIds,
        scope,
        subject,
        content: JSON.stringify(content),
      }),
    });
  }

  subscribeChatThreadClient(clientId: string, callback: (updateMessageThread: MessageThreadSummary) => void) {
    return this.client.subscribe(`/api/ws/topic/chat/thread/client/${clientId}`, (message) => {
      callback(JSON.parse(message.body) as MessageThreadSummary);
    });
  }

  subscribeChatThreadClientCareJourney(
    clientId: string,
    careJourneyInstanceId: string | null,
    callback: (updateMessageThread: MessageThreadSummary) => void,
  ) {
    const suffix = careJourneyInstanceId ? `/${careJourneyInstanceId}` : '';
    const destination = `/api/ws/topic/chat/thread/client/${clientId}/care_journey${suffix}`;
    return this.client.subscribe(destination, (message) => {
      callback(JSON.parse(message.body) as MessageThreadSummary);
    });
  }

  subscribeChatThreadClientReferral(
    clientId: string,
    referralId: string | null,
    unitId: string | null,
    callback: (updateMessageThread: MessageThreadSummary) => void,
  ) {
    const suffix = referralId && unitId ? `/${referralId}/unit/${unitId}` : '';
    const destination = `/api/ws/topic/chat/thread/client/${clientId}/referral${suffix}`;
    return this.client.subscribe(destination, (message) => {
      callback(JSON.parse(message.body) as MessageThreadSummary);
    });
  }

  subscribeChatThreadUser(userId: string, callback: (newMessageThread: MessageThreadSummary) => void) {
    return this.client.subscribe(encodeURI(`/api/ws/topic/chat/thread/user/${userId}`), (message) => {
      callback(JSON.parse(message.body) as MessageThreadSummary);
    });
  }

  subscribeToNotifications(userId: string, rosterId: number, callback: (notification: NotificationDto) => void) {
    return this.client.subscribe(encodeURI(`/api/ws/topic/notifications/roster/${rosterId}/user/${userId}`), (message) => {
      callback(JSON.parse(message.body) as NotificationDto);
    });
  }

  subscribeToMessages(userId: string, rosterId: number, callback: () => void) {
    return this.client.subscribe(encodeURI(`/api/ws/topic/messages/roster/${rosterId}/user/${userId}`), callback);
  }

  subscribeCareJourneys(careJourneyId: string, callback: () => void) {
    return this.client.subscribe(encodeURI(`/api/ws/topic/care_journeys/${careJourneyId}`), callback);
  }
}
